import { addDays, clamp, startOfDay } from 'date-fns'
import { makeAutoObservable } from 'mobx'

import {
  MAX_STUDENTS_PER_SCHEDULE,
  MAX_WAITING_SCHEDULES,
  SCHEDULE_STRINGS,
} from '../_constants/scheduleStrings'
import {
  earliestSlot,
  firstSelectableSlotOfDay,
  HORIZON_DAYS,
  isBeforeSubmitDeadline,
  isSelectableSlot,
} from '../_utils/scheduleSlot'

/** 컴포넌트가 의존하는 폼 표면. 테스트에서는 이 모양의 객체로 대신할 수 있다 */
export type ScheduleFormApi = {
  enabled: boolean
  setEnabled: (next: boolean) => void
  /** 항상 슬롯 경계(:00/:30) */
  scheduledAt: Date
  /** 날짜만 바꾼다. 그 날짜에서 현재 시간이 선택 불가면 첫 활성 슬롯으로 옮긴다 */
  setDay: (day: Date) => void
  setSlot: (slot: Date) => void
  minDate: Date
  maxDate: Date
  isSlotSelectable: (slot: Date) => boolean
  /** 선택 학생 수·대기 건수 한도를 넘으면 안내 문구. 토글 상태와 무관하게 판정하며, 문구가 있으면 토글을 켤 수 없다 */
  getLimitError: (selectedCount: number) => string | null
  deadlineError: string | null
  /** 제출 직전 5분 마감 재검증. 통과면 예약 시각, 실패면 null 을 주고 슬롯을 다시 잡는다 */
  validateForSubmit: () => Date | null
  /** 이미 걸린 예약의 시각. 없으면 null */
  existingAt: Date | null
  /** 기존 예약 시각과 다른 시각을 골랐는가 */
  isTimeChanged: boolean
  /** 기존 예약이 5분 안으로 들어와 더 이상 바꿀 수 없는가 */
  isExistingLocked: () => boolean
  /** 예약 있음 모드에서 "예약됨" 텍스트 대신 날짜·시간 필드를 열었는가 */
  editingTime: boolean
  startEditingTime: () => void
  /** 기존 예약이 취소된 뒤 호출. 예약 없음 모드(토글 OFF, 기본 슬롯)로 되돌린다 */
  clearExisting: () => void
  /** 시각 변경이 서버에 반영된 뒤 호출. 그 시각을 기존 예약 시각으로 삼아 재시도에서 다시 보내지 않게 한다 */
  commitExistingAt: (scheduledAt: Date) => void
}

type Options = {
  /** 이 학습지에 이미 걸린 대기 예약 시각. 있으면 토글 ON 고정으로 시작한다 */
  initialScheduledAt?: Date | null
  /** 학원의 대기 중 예약 건수. 새 예약의 400건 한도 판정에 쓴다 */
  waitingCount?: number
  /** 테스트에서 시각을 고정하기 위한 주입점 */
  now?: () => Date
}

const sameDayAt = (day: Date, time: Date) => {
  const next = new Date(day)
  next.setHours(time.getHours(), time.getMinutes(), 0, 0)
  return next
}

/** 예약 시각 선택 상태. 서버는 모르고 슬롯 규칙(30분 단위·60분 뒤·7일·5분 마감)만 안다 */
export class ScheduleForm implements ScheduleFormApi {
  enabled: boolean
  scheduledAt: Date
  existingAt: Date | null
  editingTime = false
  deadlineError: string | null = null
  waitingCount: number | undefined
  private readonly now: () => Date

  constructor({ initialScheduledAt = null, waitingCount, now = () => new Date() }: Options = {}) {
    this.now = now
    this.existingAt = initialScheduledAt
    this.enabled = !!initialScheduledAt
    this.scheduledAt = initialScheduledAt ?? earliestSlot(now())
    this.waitingCount = waitingCount
    makeAutoObservable<ScheduleForm, 'now'>(this, { now: false })
  }

  /** 오늘 남은 슬롯이 없는 늦은 밤에는 오늘을 고를 수 없게 가장 빠른 슬롯의 날짜부터 연다 */
  get minDate() {
    return startOfDay(earliestSlot(this.now()))
  }

  get maxDate() {
    return addDays(startOfDay(this.now()), HORIZON_DAYS - 1)
  }

  get isTimeChanged() {
    return !!this.existingAt && this.existingAt.getTime() !== this.scheduledAt.getTime()
  }

  isSlotSelectable = (slot: Date) => isSelectableSlot(slot, this.now())

  isExistingLocked = () => !!this.existingAt && !isBeforeSubmitDeadline(this.existingAt, this.now())

  getLimitError = (selectedCount: number) => {
    if (selectedCount > MAX_STUDENTS_PER_SCHEDULE) return SCHEDULE_STRINGS.limitStudents
    // 대기 건수 한도는 새 예약을 만들 때만. 이미 걸린 예약을 다루는 중에는 막지 않는다
    if (!this.existingAt && (this.waitingCount ?? 0) >= MAX_WAITING_SCHEDULES) {
      return SCHEDULE_STRINGS.limitWaiting
    }
    return null
  }

  setWaitingCount = (count: number | undefined) => {
    this.waitingCount = count
  }

  setEnabled = (next: boolean) => {
    this.enabled = next
  }

  setDay = (requested: Date) => {
    // 캘린더는 범위 밖 날짜를 막지만 직접 입력은 그대로 넘어오므로 오늘~+6일 안으로 당긴다
    const day = clamp(startOfDay(requested), { start: this.minDate, end: this.maxDate })
    const candidate = sameDayAt(day, this.scheduledAt)
    this.scheduledAt = this.isSlotSelectable(candidate)
      ? candidate
      : firstSelectableSlotOfDay(day, this.now()) ?? candidate
    this.deadlineError = null
  }

  setSlot = (slot: Date) => {
    this.scheduledAt = slot
    this.deadlineError = null
  }

  validateForSubmit = () => {
    const current = this.now()
    if (isBeforeSubmitDeadline(this.scheduledAt, current)) return this.scheduledAt
    this.deadlineError = SCHEDULE_STRINGS.deadlinePassed
    this.scheduledAt = earliestSlot(current)
    return null
  }

  startEditingTime = () => {
    this.editingTime = true
  }

  clearExisting = () => {
    this.existingAt = null
    this.enabled = false
    this.editingTime = false
    this.scheduledAt = earliestSlot(this.now())
    this.deadlineError = null
  }

  commitExistingAt = (next: Date) => {
    this.existingAt = next
    this.scheduledAt = next
  }
}
